import { useTranslationsStore } from '@/store/translations';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { isNumber, trim } from 'lodash-es';
import {
    formatDiffAmount,
    formatDiffNumber,
    getPluralTerm,
    // roundNumber,
} from '@/utils/formatters';


export const parseCellFormatting = (properties) => {
    let value = properties.content;

    if (value && value['value']) {
        value = value.value; // computed value
    }

    if (properties.formatFunc !== undefined) {
        if (isNumber(value)) {
            return properties.formatFunc(value);
        }
        return '…';
    }
    return value;
}


export const cellFormatters = computed(() => {
    const translationStore = useTranslationsStore();
    const {
        currencySymbol,
        formatCurrency,
        trans,
    } = storeToRefs(translationStore);

    function getTransNoLcu(transId) {
        return trim(trans.value(transId, false, { lcu: '' }));
    }

    function getDisplayFormatter(value, label) {
        return {
            content: value,
            label: label,
            formatFunc: (value) => value.toFixed(2),
            // formatFunc: (value) => roundNumber(value),
        };
    }

    function getInputFormatter(modelFieldName, inputType, label, properties) {
        const _properties = {
            type: 'input',
            isInput: modelFieldName !== undefined,
            label: label,
            modelFieldName: modelFieldName,
            inputType: modelFieldName ? inputType : null,
        };
        if (properties) {
            return {
                ...properties,
                ..._properties,
            };
        }
        return _properties;
    }

    function getNumberInputFormatter(modelFieldName, label, defaultValue, maxValue) {
        const fieldType = 'number';
        let properties = {};
        if (maxValue) {
            properties = {...properties, maxValue: maxValue};
        }
        if (defaultValue) {
            properties = {...properties, defaultValue: defaultValue};
        }

        return getInputFormatter(modelFieldName, fieldType, label, properties);
    }

    function getCurrencyInputFormatter(modelFieldName, label, defaultValue) {
        const fieldType = 'currency';

        let properties = {};
        if (defaultValue) {
            properties = {...properties, defaultValue};
        }

        return getInputFormatter(modelFieldName, fieldType, label, properties);
    }

    function currency(value) {
            return {
                ...getDisplayFormatter(value),
                formatFunc: formatCurrency.value,
            };
    };

    function getDiffValuesFormatter(originalValue, inputValue, label, isCurrency) {
        const inputProps = {
            content: inputValue,
            formatFunc: isCurrency ? formatDiffAmount : formatDiffNumber,
        };

        return {
            type: 'difference',
            original: isCurrency ? currency(originalValue) : getDisplayFormatter(originalValue),
            input: inputProps,
            label: label,
        }
    }

    return {
        currency: currency,
        hours: (value) => {
            return getDisplayFormatter(value, getPluralTerm(value, 'page3_hours', 'page3_hour'));
        },
        minutes: (value) => {
            return getDisplayFormatter(value, getPluralTerm(value, 'page3_minutes', 'page3_minute'));
        },
        days: (value) => {
            return getDisplayFormatter(value, getPluralTerm(value, 'page3_days', 'page3_day'));
        },
        currencyPerHour: {
            display: (value) => {
                return {
                    ...getDisplayFormatter(value, getTransNoLcu('page3_lcu_per_hour')),
                    formatFunc: formatCurrency.value,
                };
            },
            diff: (originalValue, inputValue) => {
                return getDiffValuesFormatter(originalValue, inputValue, getTransNoLcu('page3_lcu_per_hour'), true);
            },
        },
        currencyPerMonth: {
            display: (value) => {
                return {
                    ...getDisplayFormatter(value, getTransNoLcu('page3_lcu_per_month')),
                    formatFunc: formatCurrency.value,
                };
            },
            diff: (originalValue, inputValue) => {
                return getDiffValuesFormatter(originalValue, inputValue, getTransNoLcu('page3_lcu_per_month'), true);
            },
            input: (modelFieldName, defaultValue) => {
                return getCurrencyInputFormatter(
                    modelFieldName,
                    trans.value('page3_lcu_per_month', false, {
                        lcu: currencySymbol.value,
                    }),
                    defaultValue,
                );
            },
        },
        currencyPerYear: {
            display: (value) => {
                return {
                    ...getDisplayFormatter(value, getTransNoLcu('page3_lcu_per_year')),
                    formatFunc: formatCurrency.value,
                };
            },
            diff: (originalValue, inputValue) => {
                return getDiffValuesFormatter(originalValue, inputValue, getTransNoLcu('page3_lcu_per_year'), true);
            },
        },
        daysPerWeek: {
            display: (value) => {
                return getDisplayFormatter(value, getTransNoLcu('page3_days_per_week'));
            },
            diff: (originalValue, inputValue) => {
                return getDiffValuesFormatter(originalValue, inputValue, getTransNoLcu('page3_days_per_week'));
            },
            input: (modelFieldName, defaultValue, maxValue) => {
                return getNumberInputFormatter(
                    modelFieldName,
                    trans.value('page3_days_per_week'),
                    defaultValue,
                    maxValue,
                );
            },
        },
        daysPerYear: {
            display: (value) => {
                return getDisplayFormatter(value, getTransNoLcu('page3_days_per_year'));
            },
            diff: (originalValue, inputValue) => {
                return getDiffValuesFormatter(originalValue, inputValue, getTransNoLcu('page3_days_per_year'));
            },
            input: (modelFieldName, defaultValue, maxValue) => {
                return getNumberInputFormatter(
                    modelFieldName,
                    trans.value('page3_days_per_year'),
                    defaultValue,
                    maxValue,
                );
            },
        },
        hoursPerYear: {
            display: (value) => {
                return getDisplayFormatter(value, getTransNoLcu('page3_hours_per_year'));
            },
        },
        hoursPerWeek: {
            display:(value) => {
                return getDisplayFormatter(value, getTransNoLcu('page3_hours_per_week'));
            },
            diff: (originalValue, inputValue) => {
                return getDiffValuesFormatter(originalValue, inputValue, getTransNoLcu('page3_hours_per_week'));
            },
            input: (modelFieldName, defaultValue, maxValue) => {
                return getNumberInputFormatter(
                    modelFieldName,
                    trans.value('page3_hours_per_week'),
                    defaultValue,
                    maxValue,
                );
            },
        },
    };
});
